import React, { useMemo, useState, useEffect } from 'react';
import { useSpeakerCorrectionStore } from '../../stores/speakerCorrectionStore';
import { SpeakerTimeline, TimelineSegment } from './SpeakerTimeline';
import { SpeakerRoleEditor, SpeakerSummary } from './SpeakerRoleEditor';
import { SpeakerAnalysis, SpeakerRole, SpeakerCorrectionRecord } from '../../types/speakerCorrection';
import { useSpeakerAnalysis } from '../../hooks/useSpeakerAnalysis';
import { AnchorBreakdownView } from './AnchorBreakdown';

interface Props {
  encounterId: string;
  segments: TimelineSegment[];
  speakers: SpeakerSummary[];
  speakerAnalysis: SpeakerAnalysis | null;
  onPlaySegment: (segment: TimelineSegment) => void;
}

export function SpeakerReviewPanel({
  encounterId, segments, speakers, speakerAnalysis, onPlaySegment,
}: Props) {
  const isOpen = useSpeakerCorrectionStore((s) => s.isPanelOpen);
  const closePanel = useSpeakerCorrectionStore((s) => s.closePanel);
  const corrections = useSpeakerCorrectionStore((s) => s.corrections);
  const apply = useSpeakerCorrectionStore((s) => s.applyCorrection);
  const loadCorrections = useSpeakerCorrectionStore((s) => s.loadCorrections);
  const lastError = useSpeakerCorrectionStore((s) => s.lastError);

  const [selectedSegmentIds, setSelectedSegmentIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadCorrections(encounterId).catch(() => { /* surfaced via lastError */ });
    }
  }, [isOpen, encounterId, loadCorrections]);

  const indexToSegmentId = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of segments) m.set(s.index, s.id);
    return m;
  }, [segments]);

  const { unresolvedSuspectedMerges, lowConfidenceSpeakers } =
    useSpeakerAnalysis(speakerAnalysis, corrections, indexToSegmentId);

  const autoCorrects = corrections.filter((c) => c.actionType === 'auto_correct');

  async function handleRevert(c: SpeakerCorrectionRecord) {
    if (c.actionType !== 'auto_correct') return;
    const firstSegId = c.segmentIds[0];
    if (!firstSegId) return;
    const before = (c.beforeState as any)?.[firstSegId];
    if (!before) return;
    await apply(encounterId, {
      actionType: 'reassign',
      segmentIds: c.segmentIds,
      target: {
        speakerId: before.speakerId,
        role: before.speakerRole,
        displayName: before.speakerDisplayName,
      },
    });
  }

  const autoCorrectedSegmentIds = useMemo(() => {
    const ids = new Set<string>();
    for (const c of corrections) {
      if (c.actionType === 'auto_correct') {
        c.segmentIds.forEach((id) => ids.add(id));
      }
    }
    return ids;
  }, [corrections]);

  const resolvedSegmentIds = useMemo(() => {
    const ids = new Set<string>();
    // Build a quick speakerId → [segmentIds] lookup for relabel resolution.
    const segmentsBySpeaker = new Map<string, string[]>();
    for (const seg of segments) {
      const arr = segmentsBySpeaker.get(seg.speakerId) ?? [];
      arr.push(seg.id);
      segmentsBySpeaker.set(seg.speakerId, arr);
    }
    for (const c of corrections) {
      if (c.actionType === 'reassign' || c.actionType === 'split') {
        c.segmentIds.forEach((id) => ids.add(id));
      } else if (c.actionType === 'relabel') {
        // beforeState keys are the relabeled speakerIds; mark all their segments resolved.
        for (const sid of Object.keys(c.beforeState ?? {})) {
          (segmentsBySpeaker.get(sid) ?? []).forEach((segId) => ids.add(segId));
        }
      }
    }
    return ids;
  }, [corrections, segments]);

  if (!isOpen) return null;

  const existingSpeakerIds = Array.from(new Set(segments.map((s) => s.speakerId)));

  async function handleReassignToExisting(targetSpeakerId: string) {
    if (selectedSegmentIds.length === 0) return;
    const target = segments.find((s) => s.speakerId === targetSpeakerId);
    if (!target) return;
    await apply(encounterId, {
      actionType: 'reassign',
      segmentIds: selectedSegmentIds,
      target: {
        speakerId: targetSpeakerId,
        role: (target as any).speakerRole ?? 'other',
        displayName: target.speakerDisplayName,
      },
    });
    setSelectedSegmentIds([]);
  }

  async function handleSplitToNew(role: SpeakerRole, displayName: string) {
    if (selectedSegmentIds.length === 0) return;
    await apply(encounterId, {
      actionType: 'split',
      segmentIds: selectedSegmentIds,
      target: { speakerId: '__NEW__', role, displayName },
    });
    setSelectedSegmentIds([]);
  }

  async function handleRelabel(speakerId: string, newRole: SpeakerRole, newDisplayName: string) {
    await apply(encounterId, {
      actionType: 'relabel',
      segmentIds: [],
      relabel: { speakerId, role: newRole, displayName: newDisplayName },
    });
  }

  return (
    <aside
      role="dialog"
      aria-label="Speaker Review"
      className="fixed right-0 top-0 h-full w-[640px] bg-white shadow-2xl border-l z-50 flex flex-col"
    >
      <header className="flex items-center justify-between p-4 border-b">
        <h2 className="text-lg font-semibold">Speaker Review</h2>
        <button onClick={closePanel} aria-label="Close panel" className="text-gray-500 hover:text-gray-800">×</button>
      </header>

      {lastError && (
        <div className="p-2 bg-red-50 text-red-700 text-sm">Error: {lastError}</div>
      )}

      <section className="p-4 border-b">
        <h3 className="font-medium mb-2">Speakers</h3>
        <div className="space-y-2">
          {speakers.map((sp) => (
            <SpeakerRoleEditor
              key={sp.speakerId}
              speaker={sp}
              isLowConfidence={lowConfidenceSpeakers.includes(sp.speakerId)}
              onRelabel={handleRelabel}
            />
          ))}
        </div>
      </section>

      {autoCorrects.length > 0 && (
        <section className="p-4 border-b">
          <h3 className="font-medium mb-2 flex items-center gap-2">
            <span aria-hidden>⚙</span>
            System auto-corrects ({autoCorrects.length})
          </h3>
          <div className="space-y-2">
            {autoCorrects.map((c) => {
              const before = (Object.values(c.beforeState)[0] as any) ?? null;
              const after = (Object.values(c.afterState)[0] as any) ?? null;
              const merge = (speakerAnalysis?.suspectedMerges ?? [])
                .find((m) => before && m.azureSpeakerId === before.speakerId);
              return (
                <div key={c.id} className="border rounded p-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>
                      Reassigned <strong>{c.segmentIds.length}</strong> segment(s):
                      {' '}was <code>{before?.speakerId ?? '?'}</code>
                      {' '}→ now <code>{after?.speakerId ?? '?'}</code>
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRevert(c)}
                      className="text-xs px-2 py-1 border rounded hover:bg-slate-50"
                    >
                      Revert
                    </button>
                  </div>
                  {merge?.anchorBreakdown && merge.convergenceScore !== undefined && (
                    <div className="mt-2">
                      <AnchorBreakdownView
                        breakdown={merge.anchorBreakdown}
                        convergenceScore={merge.convergenceScore}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="flex-1 overflow-y-auto p-4">
        <h3 className="font-medium mb-2">
          Timeline ({selectedSegmentIds.length} selected)
        </h3>
        <SpeakerTimeline
          segments={segments}
          suspectedMerges={unresolvedSuspectedMerges}
          resolvedSegmentIds={resolvedSegmentIds}
          autoCorrectedSegmentIds={autoCorrectedSegmentIds}
          selectedSegmentIds={selectedSegmentIds}
          onSelectionChange={setSelectedSegmentIds}
          onPlaySegment={onPlaySegment}
        />
      </section>

      <footer className="p-4 border-t flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 mr-auto">
          With {selectedSegmentIds.length} segment(s) selected:
        </span>
        {existingSpeakerIds.map((sid) => (
          <button
            key={sid}
            disabled={selectedSegmentIds.length === 0}
            onClick={() => handleReassignToExisting(sid)}
            className="text-xs px-2 py-1 border rounded disabled:opacity-50"
          >
            → {sid}
          </button>
        ))}
        <button
          disabled={selectedSegmentIds.length === 0}
          onClick={() => handleSplitToNew('patient', 'Patient')}
          className="text-xs px-2 py-1 border rounded disabled:opacity-50"
        >
          → New Patient
        </button>
        <button
          disabled={selectedSegmentIds.length === 0}
          onClick={() => handleSplitToNew('family', 'Family Member')}
          className="text-xs px-2 py-1 border rounded disabled:opacity-50"
        >
          → New Family
        </button>
        <button
          disabled={selectedSegmentIds.length === 0}
          onClick={() => handleSplitToNew('interpreter', 'Interpreter')}
          className="text-xs px-2 py-1 border rounded disabled:opacity-50"
        >
          → New Interpreter
        </button>
      </footer>
    </aside>
  );
}
